import {inject, injectable} from "inversify"
import {DagNode, DagTraveller, DirectedAcyclicGraph} from "./Core/Dag"
import {PlayableAudio, SlideData, SlideMetadata} from "./Core/SlideshowData"
import {TutorialAttemptApi, TutorialAttemptDto} from "./Api/TutorialAttemptApi";
import TutorialAssets from "./UI/TutorialAssets";
import {TransitionType} from "./Core/SlideTransition";
import {PubSub} from "./Core/PubSub";
import {ISlideshowBuilder, SlideshowInformation} from "@/Core/SlideshowBuilder";
import {IAppearance, ITutorialVariables} from "@/Core/TutorialVariables";
import {TutorialState} from "@/Core/TutorialState";
import {Dictionary} from "@/Utility";
import {CreatorEvents} from "@/CreatorTools/Services/CreatorEvents";
import {SyncEvents} from "@/Synchroniser";

/**
 * Slideshow
 */
@injectable()
export class Slideshow {
    slidesGraph: DirectedAcyclicGraph<string>;
    slideTraveller: DagTraveller<string>;

    assets: TutorialAssets = new TutorialAssets();
    tutorialAttemptDetails: TutorialAttemptDto;

    private _currentSlide: SlideData|null;
    private slideshowInformation: SlideshowInformation;

    supportedLanguageCodes: string[];

    title: string = "";
    supplierText: string|null = null;
    
    constructor(
        @inject("TutorialState") private state: TutorialState,
        @inject("PubSub") private pubSub: PubSub,
        @inject("ISlideshowBuilder") private slideshowBuilder: ISlideshowBuilder,
        @inject("TutorialAttemptApi") private attemptApi: TutorialAttemptApi) {
        window.addEventListener('resize', (event) => this.pubSub.publishEvent(SlideshowEvents.Resize, event))
    }

    get attempt(): TutorialAttemptDto {
        return this.attemptApi.tutorialAttempt;
    }

    get variables(): ITutorialVariables {
        return this.attemptApi.tutorialAttempt.variables;
    }

    get appearance(): IAppearance {
        return this.attemptApi.tutorialAttempt.variables.customer.appearance;
    }

    async load(): Promise<void> {
        await this.build();
        this.pubSub.subscribeToEvent(CreatorEvents.AddSlide, (slide: {id: string}) => {
            this.state.setValue(['scenes', slide.id], {visited: false});
            this.build();
        });
        this.pubSub.subscribeToEvent(CreatorEvents.UpdateSlideMetadata, () => this.build());
        this.pubSub.subscribeToEvent(CreatorEvents.DeleteSlide, () => this.build());
        this.pubSub.subscribeToEvent(SyncEvents.ContentFinished, () => this.nextSlide());
        this.pubSub.subscribeToEvent(CreatorEvents.ReloadSlide, slideId => {
            if (slideId === this.currentSlide.id)
                this.reloadSlide();
        });
        this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntered,
            (slide: SlideData) => this.state.setValue(['scenes', slide.id, 'visited'], true));
        this.tutorialAttemptDetails = await this.attemptApi.startOrContinueAttempt();
        return this.pubSub.publishEvent(SlideshowEvents.Load, {})
            .then(() => this.pubSub.publishEvent(SlideshowEvents.SlideEntering, this.currentSlide))
            .then(() => this.startTicker())
            .catch(error => console.error(error));
    }

    async build() {
        let info = await this.slideshowBuilder.build();
        let cssElement = document.createElement("style");
        cssElement.innerHTML = info.css;
        document.head.appendChild(cssElement);
        this.slideshowInformation = info;
        this.slidesGraph = info.graph;
        this.supportedLanguageCodes = this.slideshowInformation.supportedLanguageCodes;
        this.supplierText = this.slideshowInformation.supplierText;
        this.title = this.slideshowInformation.title;
        let newTraveller = new DagTraveller<string>(this.slideshowInformation.graph);

        let questionsState: Dictionary<any> = {};

        for (let q of info.questions) {
            let items: Dictionary<{id: string, text: string}> = {};
            if (q.questionType === "choice") {
                for (let key in q.items) {
                    items[key] = {
                        id: q.items[key].code,
                        text: q.items[key].itemText
                    };
                }
            }

            questionsState[q.questionCode] = {
                type: q.questionType,
                text: q.questionText,
                min_answers: q.minAnswers,
                max_answers: q.maxAnswers,
                items: items,
                // this will need updating with tutorial attempt data
                attempted: false,
                attempts: []
            };
        }

        this.state.setValue(['questions'], questionsState);

        if (this._currentSlide == null) {
            let scenes: any = {};
            for (let s of info.slides) {
                scenes[s.id] = {visited: false};
            }
            this.state.setValue(['scenes'], scenes);

            this.slideTraveller = newTraveller;
            await this.setSlideFromHash();
        }
        else {
            let slideId = this._currentSlide.id;
            let node = newTraveller.goToNode(x => x === slideId, this.slideTraveller.branchesTaken);
            if (node?.value != slideId)
                throw "Could not restore position";
            this.slideTraveller = newTraveller;
        }
    }

    public getSlideMetadata(id: string): SlideMetadata {
        let slide = this.slideshowInformation.slides.find(s => s.id === id);
        if (slide == null)
            throw "Could not find slide";
        return slide;
    }

    private setSlideFromHash(): Promise<void> {
        if (!window.location.hash)
            return this.goToStart();

        let regex = /^#([a-zA-Z\-0-9]+)((?:![0-9]+)*)$/g;
        let matches = regex.exec(window.location.hash);
        if (matches == null) // IE11 can return null here
            return this.goToStart();

        let slideId = matches[1];
        let branchChoices = matches[2];
        if (!slideId)
            return this.goToStart();

        let branchChoicesArray = branchChoices.split("!").map(x => parseInt(x)).slice(1);
        if (!branchChoicesArray)
            branchChoicesArray = [];

        this.slideTraveller.goToNode(x => x === slideId, branchChoicesArray);
        return this.getSlideData(this.slideTraveller.currentNode.value)
            .then(data => {
                if (data == null)
                    throw "Missing slide data";
                this._currentSlide = data;
            });
    }

    private goToStart(): Promise<void> {
        while (!this.slideTraveller.atRoot) {
            this.slideTraveller.goBack();
        }

        return this.getSlideData(this.slideTraveller.currentNode.value)
            .then(data => {
                if (data == null)
                    throw "Missing slide data";
                this._currentSlide = data;
            });
    }

    private updateHash() {
        let str = '#' + this.currentSlide.id;
        for (let bt of this.slideTraveller.branchesTaken) {
            str = `${str}!${bt}`;
        }

        history.replaceState({}, document.title, window.location.pathname + window.location.search + str);
    }

    public exit() {
        this.attemptApi.exit();
    }

    private goToSlide(slideId: string) {
        let oldSlide = this.currentSlide;
        let node = this.slideTraveller.goToNextWith(x => x === slideId);
        if (!node)
            return false;

        this.transition(oldSlide, slideId);
        return true;
    }

    goBackTo(slideId: string): boolean {
        let oldSlide = this.currentSlide;
        let node = this.slideTraveller.goBack();
        while (node != null && node.value != slideId) {
            node = this.slideTraveller.goBack();
        }

        if (node == null)
            return false;

        this.transition(oldSlide, slideId);
        return true;
    }
    
    nextSlide(slideId: string|null = null, ignoreLock: boolean = false): boolean {
        if (this.slideTraveller.atEnd)
            return false;

        if (!ignoreLock && this.isNextLocked())
            return false;

        if (slideId && this.goToSlide(slideId))
            return true;

        return this.nextSlideUsingTransitions();
    }

    private nextSlideUsingTransitions(): boolean {
        let slide = this.getSlideMetadata(this.slideTraveller.currentNode.value);
        
        if (!slide.transition)
            return false;

        let slideTransition = slide.transition;
        if (!slideTransition)
            return false;

        if (slideTransition.type === TransitionType.GoTo)
            return this.goToSlide(slideTransition.id);

        if (slideTransition.type !== TransitionType.Stateful)
            return false;

        for (let r of slideTransition.rules) {
            if (this.state.evaluate(r.expr))
                this.goToSlide(r.id);
        }

        return false;
    }
    
    previousSlide(): Promise<boolean> {
        if (this.isPrevLocked())
            return Promise.resolve(false);
        
        let oldSlide = this.currentSlide;
        let node: DagNode<string>|null = null;
        let slideId: string;
        let metadata: SlideMetadata;

        do {
            node = this.slideTraveller.goBack();
            if (node == null)
                return Promise.resolve(false);

            slideId = node.value;
            metadata = this.getSlideMetadata(slideId);
        } while (metadata.skipSlide);

        return this.transition(oldSlide, slideId).then(() => true);
    }

    reloadSlide(): Promise<void> {
        return this.transition(this.currentSlide, this.currentSlide.id);
    }
    
    private transition(oldSlide: SlideData, newSlideId: string): Promise<void> {
        this.clearLocks();
        return this.pubSub.publishEvent(SlideshowEvents.SlideExiting, oldSlide)
            .then(() => this.slideshowBuilder.getContent(newSlideId))
            .then(newSlide => {
                this._currentSlide = newSlide;
                return this.pubSub.publishEvent(SlideshowEvents.SlideEntering, newSlide)
            })
            .then(() => this.updateHash());
    }
    
    private nextLocks: number[] = [];
    private nextLockNum = 0;
    
    lockNext(): number {
        this.nextLocks.push(this.nextLockNum);
        this.nextLockNum += 1;
        return this.nextLockNum - 1;
    }
    
    unlockNext(id: number) {
        let i = this.nextLocks.indexOf(id);
        if (i < 0)
            return;
            
        this.nextLocks.splice(i, 1);
    }
    
    isNextLocked(): boolean {
        return this.nextLocks.length > 0;
    }
    
    private prevLocks: number[] = [];
    private prevLockNum = 0;
    
    lockPrev(): number {
        this.prevLocks.push(this.nextLockNum);
        this.prevLockNum += 1;
        return this.prevLockNum - 1;
    }
    
    unlockPrev(id: number) {
        let i = this.prevLocks.indexOf(id);
        if (i < 0)
            return;
            
        this.prevLocks.splice(i, 1);
    }
    
    isPrevLocked(): boolean {
        return this.prevLocks.length > 0;
    }
    
    private clearLocks() {
        this.nextLocks = [];
        this.prevLocks = [];
    }
    
    startTicker() {
        setInterval(() => this.pubSub.publishEvent(SlideshowEvents.Tick, null), 50);
    }

    get currentSlide(): SlideData {
        if (this._currentSlide == null)
            throw "No current slide";
        return this._currentSlide;
    }

    get slides(): SlideMetadata[] {
        return this.slideshowInformation.slides;
    }

    get audio(): PlayableAudio[] {
        return this.slideshowInformation.audio;
    }

    get nextSlides(): SlideMetadata[] {
        let slide = this.getSlideMetadata(this.slideTraveller.currentNode.value);
        if (slide.transition.type === TransitionType.EndOfTutorial)
            return [];
        let slideIds: string[] = [];
        if (slide.transition.type === TransitionType.GoTo)
            slideIds = [slide.transition.id];
        if (slide.transition.type === TransitionType.Choice)
            slideIds = slide.transition.choices.map(c => c.id);
        if (slide.transition.type === TransitionType.Stateful)
            slideIds = slide.transition.rules.map(c => c.id);
        return slideIds.map(id => this.getSlideMetadata(id));
    }

    getSlideData(id: string): Promise<SlideData|null> {
        if (id === this._currentSlide?.id)
            return Promise.resolve(this.currentSlide);

        return this.slideshowBuilder.getContent(id);
    }

    slideEntered() {
        let slideData = this.currentSlide;
        return this.pubSub.publishEvent(SlideshowEvents.SlideEntered, slideData);
    }
}

export interface SlideGroup {
    slides: SlideData[];
    groupId: string;
}

export enum SlideshowEvents {
    Load = "Load",
    Tick = "Tick",
    SlideEntering = "SlideEntering",
    SlideEntered = "SlideEntered",
    SlideExiting = "SlideExiting",
    Resize = "Resize"
}